import { awaitingLease } from '../../lib/onboarding';
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Wrench, Calendar as CalendarIcon, ChevronRight, CircleAlert } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { FlightActions } from '../../components/FlightActions';
import { FlightEditModal } from '../../components/FlightEditModal';
import { fmtUSD, fmtHours, rateFor } from '../../lib/billingCycles';
import { countCalendarNights } from '../../lib/bookingRules';
import { mxStatus, MX_LABEL, MX_TONE } from '../../lib/maintenance';
import { SQUAWK_SEV_LABEL } from '../../lib/squawks';
import {
  DEMO_AIRCRAFT,
  DEMO_LESSEES,
  DEMO_BOOKINGS,
  DEMO_DOCUMENTS,
  DEMO_SQUAWKS,
  DEMO_FLIGHTS,
  DEMO_MAINTENANCE,
  DEMO_TRANSACTIONS,
  lesseeByUid,
} from '../../lib/demoData';
import { TRACKED_REG } from '../../lib/flightTrack';
import type {
  AircraftDoc,
  BookingDoc,
  DocumentDoc,
  FlightDoc,
  MaintenanceItemDoc,
  SquawkDoc,
  TransactionDoc,
  UserDoc,
} from '../../lib/types';

const TONE_CLS: Record<'ok' | 'warning' | 'danger', string> = {
  ok: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
};
const SEV_CLS: Record<string, string> = {
  grounding: 'bg-red-100 text-red-800',
  should_fix_soon: 'bg-amber-100 text-amber-800',
  informational: 'bg-slate-100 text-slate-700',
};

export function AdminDashboard() {
  const { demoMode } = useAuth();
  const [aircraft, setAircraft] = useState<AircraftDoc | null>(null);
  const [bookings, setBookings] = useState<BookingDoc[]>([]);
  const [squawks, setSquawks] = useState<SquawkDoc[]>([]);
  const [flights, setFlights] = useState<FlightDoc[]>([]);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [documents, setDocuments] = useState<DocumentDoc[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceItemDoc[]>([]);
  const [transactions, setTransactions] = useState<TransactionDoc[]>([]);
  const [editing, setEditing] = useState<FlightDoc | null>(null);

  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT[0]);
      setBookings(DEMO_BOOKINGS);
      setSquawks(DEMO_SQUAWKS);
      setFlights(DEMO_FLIGHTS);
      setUsers(DEMO_LESSEES);
      setDocuments(DEMO_DOCUMENTS);
      setMaintenance(DEMO_MAINTENANCE);
      setTransactions(DEMO_TRANSACTIONS);
      return;
    }
    if (!db) return;
    const subs = [
      onSnapshot(collection(db, 'aircraft'), (s) => setAircraft((s.docs[0]?.data() as AircraftDoc) ?? null)),
      onSnapshot(collection(db, 'bookings'), (s) => setBookings(s.docs.map((d) => d.data() as BookingDoc))),
      onSnapshot(collection(db, 'squawks'), (s) => setSquawks(s.docs.map((d) => d.data() as SquawkDoc))),
      onSnapshot(collection(db, 'flights'), (s) => setFlights(s.docs.map((d) => ({ ...(d.data() as FlightDoc), flightId: d.id })))),
      onSnapshot(collection(db, 'users'), (s) => setUsers(s.docs.map((d) => d.data() as UserDoc))),
      onSnapshot(collection(db, 'documents'), (s) => setDocuments(s.docs.map((d) => d.data() as DocumentDoc))),
      onSnapshot(collection(db, 'maintenance'), (s) => setMaintenance(s.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))),
      onSnapshot(collection(db, 'transactions'), (s) => setTransactions(s.docs.map((d) => d.data() as TransactionDoc))),
    ];
    return () => subs.forEach((u) => u());
  }, [demoMode]);

  const d = useMemo(() => {
    const now = new Date();
    const lessees = users.filter((u) => u.role === 'lessee');
    const openSquawks = squawks.filter((s) => s.status !== 'resolved');
    const grounding = openSquawks.filter((s) => s.isGrounding);
    const grounded = grounding.length > 0 || Boolean(aircraft?.groundedByOwner);
    const tach = aircraft?.currentTach ?? 0; // maintenance tracks on tach
    const mxFlagged = maintenance
      .map((m) => ({ m, st: mxStatus(m, tach) }))
      .filter((x) => x.st.level >= 1)
      .sort((a, b) => (a.st.deferred === b.st.deferred ? b.st.level - a.st.level : a.st.deferred ? 1 : -1));
    const upcoming = bookings
      .filter((b) => b.startDateTime?.toDate?.() > now && b.status !== 'cancelled' && b.status !== 'denied')
      .sort((a, b) => a.startDateTime.toDate().getTime() - b.startDateTime.toDate().getTime())
      .slice(0, 5);
    const recentFlights = [...flights].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5);
    const monthRevenue = transactions
      // Exclude grounding credits + refunds — they're money owed back to the lessee,
      // not revenue earned (their positive amount would otherwise inflate the total).
      .filter((t) => t.status === 'succeeded' && t.type !== 'grounding_credit' && t.type !== 'refund' && t.createdAt?.toDate?.().getMonth() === now.getMonth() && t.createdAt.toDate().getFullYear() === now.getFullYear())
      .reduce((s, t) => s + (t.amount || 0), 0);
    // Casey 11/3/26: "potential revenue based on flights that have been booked but not
    // completed". Every live booking that hasn't ended yet, priced at the pilot's own
    // rate (discovery = the guest's wet rate). Booked block time, not Hobbs, so it's a
    // ceiling: a 5-hour block usually flies 2. Overnights count the lease's daily
    // minimum per night, not 24 hours a day. The owner's own time and maintenance holds are $0.
    const pending = bookings.filter(
      (b) =>
        b.endDateTime?.toDate?.() > now &&
        !['cancelled', 'denied', 'completed'].includes(b.status) &&
        !b.maintenanceLabel
    );
    let potentialRevenue = 0;
    let potentialHours = 0;
    let potentialCount = 0;
    if (aircraft) {
      for (const b of pending) {
        const pilot = users.find((u) => u.uid === b.lesseeUid);
        if (!pilot || pilot.role === 'admin') continue;
        const rate = b.flightPurpose === 'discovery' ? aircraft.discoveryRate ?? 170 : rateFor(pilot, aircraft);
        if (!rate) continue;
        const start = b.startDateTime.toDate();
        const end = b.endDateTime.toDate();
        const nights = countCalendarNights(start, end);
        const hrs = nights > 0
          ? nights * (aircraft.awayFromBaseHoursPerDay || 0)
          : (end.getTime() - start.getTime()) / 3600000;
        potentialHours += hrs;
        potentialRevenue += hrs * rate;
        potentialCount += 1;
      }
    }
    const annual = aircraft?.annualDueDate ? new Date(aircraft.annualDueDate) : null;
    const annualDays = annual ? Math.round((annual.getTime() - now.getTime()) / 86400000) : null;
    return {
      lessees,
      activeLessees: lessees.filter((u) => u.status === 'active').length,
      newRegistrations: lessees.filter((u) => u.status === 'pending_approval').length,
      pendingApprovals: lessees.filter((u) => u.status === 'verification_pending').length,
      pendingDocs: documents.filter((x) => x.verificationStatus === 'pending').length,
      // Documents done but the executed lease not recorded — the DocuSign is out or
      // back. Carter sat here a day with a Completed envelope and nothing said so.
      awaitingLease: lessees.filter(awaitingLease),
      groundingCount: grounding.length,
      pastDue: lessees.filter((u) => u.paymentPastDue || u.paymentFailedFlag),
      openSquawks,
      grounded,
      mxFlagged,
      upcoming,
      recentFlights,
      monthRevenue,
      potentialRevenue,
      potentialHours,
      potentialCount,
      annualDays,
    };
  }, [aircraft, bookings, squawks, flights, users, documents, maintenance, transactions]);

  return (
    <>
      <PageHeader
        title="Owner dashboard"
        subtitle={`Live status of ${TRACKED_REG} and everything happening in the portal.`}
      />

      {/* Book and log — the two things the owner actually comes here to do. */}
      <div className="mt-4">
        <FlightActions bookTo="/admin/book" logTo="/admin/log-flight" />
      </div>

      {/* Action needed — the one place to see everything waiting on you. */}
      <ActionNeeded
        items={[
          d.groundingCount > 0 && {
            label: `Aircraft grounded — ${d.groundingCount} grounding squawk${d.groundingCount > 1 ? 's' : ''} to resolve`,
            to: '/admin/squawks',
            danger: true,
          },
          d.newRegistrations > 0 && {
            label: `${d.newRegistrations} new registration${d.newRegistrations > 1 ? 's' : ''} to approve`,
            to: '/admin/lessees?filter=pending',
          },
          d.pendingApprovals > 0 && {
            label: `${d.pendingApprovals} pilot${d.pendingApprovals > 1 ? 's' : ''} ready to activate`,
            to: '/admin/lessees?filter=verification',
          },
          d.awaitingLease.length > 0 && {
            label:
              d.awaitingLease.length === 1
                ? `${d.awaitingLease[0].personal?.firstName ?? 'A pilot'}: approve when the DocuSign is back`
                : `${d.awaitingLease.map((u) => u.personal?.firstName ?? '?').join(', ')}: approve when the DocuSign is back`,
            to:
              d.awaitingLease.length === 1
                ? `/admin/lessees/${d.awaitingLease[0].uid}`
                : '/admin/lessees',
          },
          d.pendingDocs > 0 && {
            label: `${d.pendingDocs} document${d.pendingDocs > 1 ? 's' : ''} to verify`,
            to: '/admin/verify',
          },
          d.pastDue.length > 0 && {
            label: `${d.pastDue.length} past-due pilot${d.pastDue.length > 1 ? 's' : ''}`,
            to: '/admin/lessees',
            danger: true,
          },
        ].filter(Boolean) as ActionItem[]}
      />

      {/* Aircraft status hero */}
      <div className={`card mt-5 overflow-hidden ${d.grounded ? 'ring-2 ring-red-400' : ''}`}>
        <div className={`px-5 py-4 sm:px-6 ${d.grounded ? 'bg-red-600' : 'bg-brand'} text-white`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Plane size={18} className={d.grounded ? 'text-white' : 'text-sky-100'} />
              <span className="font-display text-lg font-semibold">
                {aircraft?.tailNumber ?? TRACKED_REG}
              </span>
              <span className="text-sm text-white/70">
                {aircraft ? `${aircraft.year} ${aircraft.make} ${aircraft.model}` : ''}
              </span>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${d.grounded ? 'bg-white text-red-700' : 'bg-emerald-400/20 text-emerald-100'}`}>
              {d.grounded ? '● Grounded' : '● Active'}
            </span>
          </div>
          {d.grounded && (
            <p className="mt-1 text-sm text-white/90">
              Open grounding squawk — booking &amp; flying are blocked until you resolve it.
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 px-5 py-4 sm:grid-cols-4 sm:px-6">
          <Stat label="Hobbs" value={aircraft ? aircraft.currentHobbs.toFixed(1) : '—'} />
          <Stat label="Tach" value={aircraft ? aircraft.currentTach.toFixed(1) : '—'} />
          <Stat
            label="Annual due"
            value={d.annualDays != null ? (d.annualDays < 0 ? `Overdue ${Math.abs(d.annualDays)}d` : `${d.annualDays}d`) : '—'}
            tone={d.annualDays != null && d.annualDays < 30 ? (d.annualDays < 7 ? 'danger' : 'warning') : 'default'}
          />
          <Stat label="Open squawks" value={String(d.openSquawks.length)} tone={d.grounded ? 'danger' : d.openSquawks.length ? 'warning' : 'default'} />
        </div>
        <Link to="/admin/aircraft" className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm text-navy-700 hover:bg-slate-50 sm:px-6">
          Manage aircraft &amp; maintenance <ChevronRight size={16} />
        </Link>
      </div>

      {/* Action tiles */}
      <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile label="New registrations" count={d.newRegistrations} to="/admin/lessees?filter=pending" />
        <Tile label="Pilots to activate" count={d.pendingApprovals} to="/admin/lessees?filter=verification" />
        <Tile label="Documents to verify" count={d.pendingDocs} to="/admin/verify" />
        <Tile label="Past-due pilots" count={d.pastDue.length} to="/admin/lessees" danger />
      </div>

      {/* Money + roster strip */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card p-5">
          <div className="text-xs uppercase tracking-wider text-ink-subtle">Revenue this month</div>
          <div className="mt-1 font-display text-3xl font-bold text-navy-900">{fmtUSD(d.monthRevenue)}</div>
          <Link to="/admin/revenue" className="text-sm text-navy-700 hover:underline">View revenue →</Link>
        </div>
        <div className="card p-5">
          <div className="text-xs uppercase tracking-wider text-ink-subtle">Booked, not flown yet</div>
          <div className="mt-1 font-display text-3xl font-bold text-navy-900">up to {fmtUSD(d.potentialRevenue)}</div>
          <div className="text-xs text-ink-muted">
            {d.potentialCount} booking{d.potentialCount === 1 ? '' : 's'} · {fmtHours(d.potentialHours)} booked. Real Hobbs usually runs less.
          </div>
          <Link to="/admin/bookings" className="text-sm text-navy-700 hover:underline">View bookings →</Link>
        </div>
        <div className="card p-5">
          <div className="text-xs uppercase tracking-wider text-ink-subtle">Active pilots</div>
          <div className="mt-1 font-display text-3xl font-bold text-navy-900">{d.activeLessees}</div>
          <Link to="/admin/lessees" className="text-sm text-navy-700 hover:underline">Manage pilots →</Link>
        </div>
      </div>

      {/* Two-column detail */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Upcoming bookings */}
        <Panel title="Upcoming bookings" icon={<CalendarIcon size={16} />} to="/admin/bookings">
          {d.upcoming.length === 0 ? (
            <Empty>No upcoming bookings.</Empty>
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.upcoming.map((b) => {
                const u = users.find((x) => x.uid === b.lesseeUid) ?? lesseeByUid(b.lesseeUid);
                return (
                  <li key={b.bookingId} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-navy-900">
                        {u?.personal?.firstName ?? 'Pilot'} {u?.personal?.lastName ?? ''}
                      </div>
                      <div className="truncate text-xs text-ink-muted">
                        {b.startDateTime.toDate().toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        {b.expectedDestinations ? ` · ${b.expectedDestinations}` : ''}
                      </div>
                    </div>
                    <span className="badge-success">Confirmed</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* Recent flights */}
        <Panel title="Recent flights" icon={<Plane size={16} />} to="/admin/flights">
          {d.recentFlights.length === 0 ? (
            <Empty>No flights logged yet.</Empty>
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.recentFlights.map((f) => {
                const u = users.find((x) => x.uid === f.lesseeUid) ?? lesseeByUid(f.lesseeUid);
                return (
                  <li key={f.flightId} onClick={() => setEditing(f)} className="flex cursor-pointer items-center justify-between gap-3 py-2.5 active:opacity-60">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-navy-900">{u?.personal?.firstName ?? 'Pilot'} · {f.date}</div>
                      <div className="truncate text-xs text-ink-muted">{f.departureAirport} → {f.destinations.join(' → ')}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-navy-900">{fmtHours(f.hoursFlown)}</div>
                      <div className="text-xs text-emerald-700">${f.totalCharge.toFixed(2)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* Open squawks */}
        <Panel title="Open squawks" icon={<CircleAlert size={16} />} to="/admin/squawks">
          {d.openSquawks.length === 0 ? (
            <Empty>No open squawks. 🎉</Empty>
          ) : (
            <ul className="space-y-2">
              {d.openSquawks.map((s) => (
                <li key={s.squawkId} className="flex items-start gap-2.5">
                  <span className={`mt-0.5 shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEV_CLS[s.severity] ?? SEV_CLS.informational}`}>
                    {SQUAWK_SEV_LABEL[s.severity]}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-ink">{s.description}</div>
                    <div className="text-xs text-ink-muted">{s.dateDiscovered}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Maintenance due */}
        <Panel title="Maintenance coming due" icon={<Wrench size={16} />} to="/admin/aircraft">
          {d.mxFlagged.length === 0 ? (
            <Empty>Nothing due soon.</Empty>
          ) : (
            <ul className="space-y-2">
              {d.mxFlagged.map(({ m, st }) => (
                <li key={m.itemId} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-ink">{m.label}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      st.deferred ? 'bg-slate-100 text-slate-600' : TONE_CLS[MX_TONE[st.level]]
                    }`}
                  >
                    {st.deferred ? 'Held' : MX_LABEL[st.level]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      {editing && <FlightEditModal flight={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

type ActionItem = { label: string; to: string; danger?: boolean };
/**
 * Renders nothing when there is nothing to do.
 *
 * There used to be a green "you're all caught up" panel here. Absence already
 * says that, and a box that is always present stops being read — the same reason
 * AirbornePill shows nothing when the aircraft is parked. Keeping the slot empty
 * is what makes the amber block mean something when it does appear.
 */
function ActionNeeded({ items }: { items: ActionItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-amber-200">
      <div className="flex items-center gap-2 bg-amber-50 px-5 py-3">
        <CircleAlert size={16} className="text-amber-700" />
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-amber-800">
          Action needed ({items.length})
        </h2>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.to + it.label}>
            <Link to={it.to} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50">
              <span className={`text-sm font-medium ${it.danger ? 'text-red-700' : 'text-navy-900'}`}>{it.label}</span>
              <ChevronRight size={16} className="shrink-0 text-ink-subtle" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tile({ label, count, to, danger }: { label: string; count: number; to: string; danger?: boolean }) {
  const hot = count > 0;
  return (
    <Link to={to} className="card group p-4 transition hover:shadow-lift">
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className={`font-display text-3xl font-bold ${danger && hot ? 'text-red-700' : 'text-navy-900'}`}>{count}</span>
        <ChevronRight size={16} className="text-ink-subtle transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function Panel({ title, icon, to, children }: { title: string; icon: React.ReactNode; to: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold text-navy-900">
          <span className="text-navy-700">{icon}</span>
          {title}
        </h2>
        <Link to={to} className="text-sm text-navy-700 hover:underline">All</Link>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-4 text-center text-sm text-ink-muted">{children}</div>;
}

function Stat({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'warning' | 'danger' }) {
  const cls = tone === 'danger' ? 'text-red-700' : tone === 'warning' ? 'text-amber-700' : 'text-navy-900';
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={`font-display text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
